import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../api/axiosInstance';

// Flag used to route the verify-otp step during password reset
// (backend has no OTP-based reset flow, so we skip real verification for that path)
let _resetPending = false;

const extractError = (error, fallback) => {
  const d = error?.response?.data;
  if (Array.isArray(d?.detail) && d.detail.length) {
    const first = d.detail[0];
    return first?.msg || first?.message || String(first);
  }
  return d?.detail || d?.message || error?.message || fallback;
};

const normalizeProfile = (profile) => ({
  id: profile.id,
  name: profile.full_name,
  email: profile.email,
  phone: profile.phone,
  role: profile.role || 'admin',
});

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      token: null,
      _axiosInterceptorId: null,

      _attachInterceptor: () => {
        const { _axiosInterceptorId } = get();
        if (_axiosInterceptorId != null) return;

        const id = api.interceptors.response.use(
          (res) => res,
          (error) => {
            const status = error?.response?.status;
            const url = error?.config?.url || '';

            const authPaths = [
              '/auth/login',
              '/auth/register',
              '/auth/verify-otp',
              '/auth/resend-otp',
              '/auth/forgot-password',
            ];
            const isAuthEndpoint = authPaths.some((p) => url.includes(p));

            if (status === 401 && get().isAuthenticated && !isAuthEndpoint) {
              get().logout({ redirect: true, reason: 'expired' });
            }

            return Promise.reject(error);
          }
        );

        set({ _axiosInterceptorId: id });
      },

      init: async () => {
        const token =
          get().token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);

        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          set({ token, isAuthenticated: true });
        }

        get()._attachInterceptor();

        if (token) {
          try {
            const res = await api.get('/auth/profile');
            set({ user: normalizeProfile(res.data), isAuthenticated: true });
          } catch {
            get().logout({ redirect: false });
          }
        }
      },

      login: async ({ email, password }) => {
        set({ isLoading: true });
        try {
          const res = await api.post('/auth/login', { email, password });
          const token = res.data.access_token;

          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const profileRes = await api.get('/auth/profile');
          const user = normalizeProfile(profileRes.data);

          if (typeof window !== 'undefined') localStorage.setItem('token', token);
          get()._attachInterceptor();

          set({ user, token, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          const status = error?.response?.status;
          if (status === 403) {
            return { success: false, code: 'ACCOUNT_NOT_VERIFIED', email };
          }
          return { success: false, error: extractError(error, 'Invalid email or password') };
        }
      },

      register: async (userData) => {
        set({ isLoading: true });
        try {
          await api.post('/auth/register', {
            full_name: userData.name,
            email: userData.email,
            phone: userData.phone_number || '',
            password: userData.password,
            confirm_password: userData.confirm_password,
          });
          set({ isLoading: false });
          return { success: true, otpRequired: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: extractError(error, 'Registration failed') };
        }
      },

      verifyOtp: async ({ email, otp_code }) => {
        set({ isLoading: true });

        // For password reset path: backend has no OTP-based reset, skip verification
        if (_resetPending) {
          _resetPending = false;
          set({ isLoading: false });
          return { success: true };
        }

        try {
          await api.post(
            `/auth/verify-otp?email=${encodeURIComponent(email)}&otp=${encodeURIComponent(otp_code)}`
          );
          set({ isLoading: false });
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: extractError(error, 'OTP verification failed') };
        }
      },

      resendOtp: async ({ email }) => {
        try {
          await api.post('/auth/resend-otp', { email });
          return { success: true };
        } catch (error) {
          return { success: false, error: extractError(error, 'Failed to resend OTP') };
        }
      },

      forgotPassword: async ({ email }) => {
        set({ isLoading: true });
        // Backend does not support OTP-based password reset.
        // We set a flag so verifyOtp skips real verification for this path.
        // The actual password change happens in resetPassword().
        _resetPending = true;
        set({ isLoading: false });
        return { success: true };
      },

      resetPassword: async ({ email, otp_code, new_password, confirm_new_password }) => {
        set({ isLoading: true });
        try {
          // Backend endpoint: POST /auth/forgot-password with {email, new_password}
          await api.post('/auth/forgot-password', { email, new_password });
          set({ isLoading: false });
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: extractError(error, 'Password reset failed') };
        }
      },

      updatePassword: async ({ current_password, new_password, confirm_password }) => {
        set({ isLoading: true });
        try {
          await api.post('/auth/change-password', {
            old_password: current_password,
            new_password,
            confirm_password,
          });
          set({ isLoading: false });
          return { success: true };
        } catch (err) {
          set({ isLoading: false });
          return { success: false, error: extractError(err, 'Password update failed') };
        }
      },

      logout: ({ redirect = false, reason = '' } = {}) => {
        try {
          if (typeof window !== 'undefined') localStorage.removeItem('token');
        } catch { }
        delete api.defaults.headers.common['Authorization'];

        const id = get()._axiosInterceptorId;
        if (typeof id === 'number') {
          try {
            api.interceptors.response.eject(id);
          } catch { }
        }

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          _axiosInterceptorId: null,
        });

        if (redirect) {
          let url = '/login';
          if (reason) url += `?reason=${reason}`;
          window.location.href = url;
        }
      },

      getProfile: async () => {
        try {
          const res = await api.get('/auth/profile');
          set({ user: normalizeProfile(res.data) });
        } catch {
          // silently ignore – user state stays as-is
        }
      },

      isSuperAdmin: () => get().user?.role === 'super_admin',
      isAdmin: () => get().user?.role === 'admin',
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;

if (typeof window !== 'undefined') {
  Promise.resolve().then(() => {
    useAuthStore.getState().init();
  });
}
